import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp, collectTasksFromProjects } from '../context/AppContext';
import AppLayout from '../components/layout/AppLayout';
import { EditTaskModal, ViewTaskModal } from '../components/kanban/KanbanBoard';
import { Button, Badge, EmptyState } from '../components/ui';
import { STATUSES, PRIORITIES } from '../data/constants';

const FILTER_CONFIG = {
  all: {
    title: 'All Tasks',
    subtitle: 'Every task across your projects',
    emoji: '📋',
    accent: '#3b82f6',
    status: null,
  },
  done: {
    title: 'Completed Tasks',
    subtitle: 'Tasks marked as done',
    emoji: '✅',
    accent: '#10b981',
    status: 'done',
  },
  inprogress: {
    title: 'In Progress Tasks',
    subtitle: 'Tasks currently in progress',
    emoji: '⚡',
    accent: '#f59e0b',
    status: 'inprogress',
  },
};

function TaskRow({ item, onView, onEdit }) {
  const statusMeta = STATUSES.find(s => s.id === item.status) ?? STATUSES[0];
  const priorityMeta = PRIORITIES[item.priority] ?? PRIORITIES.medium;

  return (
    <div
      style={{
        width: '100%', background: '#fff',
        border: '1.5px solid #f1f5f9', borderRadius: 14, padding: '1rem 1.25rem',
        transition: 'all 0.15s',
        display: 'flex', alignItems: 'flex-start', gap: '1rem',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = item.projectColor + '55';
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = '#f1f5f9';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <button
        type="button"
        onClick={() => onView(item)}
        style={{
          flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: '1rem',
          background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
        }}
      >
      <div style={{
        width: 10, height: 10, borderRadius: '50%', background: item.projectColor,
        marginTop: 6, flexShrink: 0,
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 15, fontWeight: 700, color: '#0f0f1a', marginBottom: 4 }}>
          {item.title}
        </p>
        {item.desc && (
          <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 8, lineHeight: 1.45 }}>
            {item.desc}
          </p>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: item.projectColor }}>
            {item.isSubproject ? `${item.rootProjectTitle} → ${item.projectTitle}` : item.projectTitle}
          </span>
          <Badge label={statusMeta.label} color={statusMeta.color} bg={statusMeta.bg} />
          <Badge label={priorityMeta.label} color={priorityMeta.color} bg={priorityMeta.bg} />
        </div>
      </div>
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
        <button
          type="button"
          title="Edit task"
          onClick={e => { e.stopPropagation(); onEdit(item); }}
          style={{
            width: 32, height: 32, borderRadius: 8, border: '1.5px solid #e2e8f0',
            background: '#f8fafc', color: '#64748b', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default function TasksListPage() {
  const { projects, updateTask } = useApp();
  const navigate = useNavigate();
  const { filter = 'all' } = useParams();
  const [viewingTask, setViewingTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);

  const config = FILTER_CONFIG[filter] ?? FILTER_CONFIG.all;

  const tasks = useMemo(
    () => collectTasksFromProjects(projects, config.status),
    [projects, config.status],
  );

  return (
    <AppLayout>
      <div className="page-content page-content--narrow">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#64748b', fontSize: 14, marginBottom: '1.5rem', padding: 0,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
          Back to Dashboard
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: '2rem' }}>
          <div style={{ fontSize: 36 }}>{config.emoji}</div>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#0f0f1a', letterSpacing: '-0.03em', marginBottom: 4 }}>
              {config.title}
            </h1>
            <p style={{ fontSize: 14, color: '#64748b' }}>
              {config.subtitle} · <strong style={{ color: config.accent }}>{tasks.length}</strong> task{tasks.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {tasks.length === 0 ? (
          <EmptyState
            icon={config.emoji}
            title="No tasks here"
            desc="Tasks matching this filter will appear when you add them to your projects."
            action={<Button variant="secondary" onClick={() => navigate('/dashboard')}>Go to projects</Button>}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {tasks.map(item => (
              <TaskRow
                key={`${item.projectId}-${item.id}`}
                item={item}
                onView={setViewingTask}
                onEdit={setEditingTask}
              />
            ))}
          </div>
        )}
      </div>

      {viewingTask && (
        <ViewTaskModal
          task={viewingTask}
          projectColor={viewingTask.projectColor}
          projectTitle={viewingTask.projectTitle}
          rootProjectTitle={viewingTask.rootProjectTitle}
          isSubproject={viewingTask.isSubproject}
          onClose={() => setViewingTask(null)}
          onOpenProject={() => {
            setViewingTask(null);
            navigate(`/project/${viewingTask.projectId}`);
          }}
          onEdit={() => {
            setEditingTask(viewingTask);
            setViewingTask(null);
          }}
        />
      )}

      {editingTask && (
        <EditTaskModal
          task={editingTask}
          projectColor={editingTask.projectColor}
          onClose={() => setEditingTask(null)}
          onSave={changes => updateTask(editingTask.projectId, editingTask.id, changes)}
        />
      )}
    </AppLayout>
  );
}
